import { virtualInput } from './virtualInput.js';
import { isInputLocked } from './inputLock.js';
import { audioManager } from '../audio/AudioManager.js';
import { bikeSession } from './bikeSession.js';
import { realtime } from '../api/realtime.js';
import { showToast } from './Toast.js';
import { preloader } from '../scenes/PreloadScene.js';
import { ASSETS } from '../assetManifest.mjs';
import { centreForFeet } from './outdoorScale.mjs';
import { ensurePoseSheets } from './walkAnim.js';
import { poseKeys } from './poseLayers.mjs';
import { isSeatFurniture } from './furnitureSpots.mjs';
import {
  spotsFor, takenPerches, nearestFreeSpot, tappedSpot, parsePerch, refreshOutcome, seatAction, standFeet, PERCH_RADIUS, TAP_SPOT_RADIUS,
  SIT_LABEL, STAND_LABEL,
} from './furnitureSeatsLogic.mjs';

export { SIT_LABEL, STAND_LABEL };
const CLICK_SIT_RANGE = 170;   // feet distance within which tapping a spot sits you (the altana's)
export const TAKEN_TOAST = 'To miejsce jest zajęte';

function isTextInputFocused() {
  const tag = document.activeElement?.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA';
}

// Sitting on the sofas and lying in the beds of one room (sofas and beds, 2026-10-29) — yours or a friend's.
// Near a free spot the action button offers "Usiądź" / "Połóż się"; sitting turns the walker off, pins the avatar's
// pose anchor on the spot and tells the room {mode: 'rest', perch}. Moving, "Wstań", or the piece changing under you
// stands you up. Peers resting on a perch are drawn by RemoteAvatar (opts.perchAnchor). The server keeps the first
// claim on a perch and answers a later one with `perch_taken`.
//
// getPlacements: () => [{placement, sprite}] — the room's drawn pieces. getPeers: () => iterable of {mode, perch}.
// tapZones: a tap on a free spot sits you (visits — at home a tap on the piece opens its menu, which has the same).
export class FurnitureSeats {
  constructor(scene, { natalka, walker, getPlacements, getPeers, onBroadcast = (p) => realtime.move(p), tapZones = false }) {
    this.scene = scene;
    this.natalka = natalka;
    this.walker = walker;
    this._getPlacements = getPlacements;
    this._getPeers = getPeers ?? (() => []);
    this._broadcast = onBroadcast;
    this._tapZones = tapZones;
    this.perch = null;
    this._mySpot = null;
    this._entries = new Map();     // placementId -> {placement, box, sprite}
    this._fronts = new Map();      // placementId -> front overlay image
    this._zones = [];
    this._offer = null;
    this._mine = null;             // the action-button entry this registered (the slot is shared: placing, the altana)
    this._cursors = scene.input.keyboard?.createCursorKeys();
    this._wasd = scene.input.keyboard?.addKeys({ W: 'W', A: 'A', S: 'S', D: 'D' });
    this._offTaken = realtime.on('perch_taken', (m) => this.onPerchTaken(m.perch));
    this._onUpdate = () => this.update();
    this._onPost = () => this.sortDepths();
    scene.events.on('update', this._onUpdate);
    scene.events.on('postupdate', this._onPost);
    scene.events.once('shutdown', () => this.destroy());
    this.refresh();
  }

  get isSeated() { return this.perch !== null; }

  /** Map placementId -> {placement, box} of this room's sofas and beds (what perchAnchor reads). */
  boxes() { return this._entries; }

  _read() {
    const m = new Map();
    for (const { placement, sprite } of this._getPlacements() ?? []) {
      if (!isSeatFurniture(placement) || !sprite?.active) continue;
      m.set(placement.id, { placement, sprite, box: { x: sprite.x, y: sprite.y, w: sprite.displayWidth, h: sprite.displayHeight, scale: sprite.perspectiveScale ?? 1 } });
    }
    return m;
  }

  _spots() {
    const out = [];
    for (const e of this._entries.values()) out.push(...spotsFor(e.placement, e.box));
    return out;
  }

  _taken() { return takenPerches(this._getPeers() ?? []); }

  // What decides where the spots are; when it changes the fronts and zones are hung again.
  static _signature(entries) {
    return [...entries].map(([id, { placement: p, box: b }]) => `${id}:${p.item}:${!!p.flipped}:${b.x},${b.y},${b.w},${b.h}`).join('|');
  }

  /** The room's pieces were drawn again: re-read them, re-hang the fronts and zones, stand up if ours changed.
   * update() calls it by itself when a piece moves, flips, is added / removed or its picture arrives late. */
  refresh(next = this._read()) {
    this._sig = FurnitureSeats._signature(next);
    if (this.perch) {
      const id = parsePerch(this.perch)?.placementId;
      if (refreshOutcome(this._entries.get(id), next.get(id)) === 'stand') { this._entries = next; this.stand(); }
    }
    this._entries = next;
    this._buildFronts();
    this._buildZones();
    // Sitting should not wait on a download: fetch our pose the moment the room has somewhere to sit.
    if (next.size) ensurePoseSheets(this.scene, poseKeys(this.natalka.character ?? 'natalka', this.natalka.equipped, this.natalka.look), () => {});
  }

  // The part of a piece that is in front of whoever sits on it (a sofa's seat front, a bed's duvet): the same picture
  // size as the piece, drawn just above the people on it.
  _buildFronts() {
    for (const img of this._fronts.values()) img.destroy();
    this._fronts.clear();
    for (const [id, e] of this._entries) {
      const key = `furniture_${e.placement.item}_front`;
      if (!(key in ASSETS) && !this.scene.textures.exists(key)) continue;
      const img = this.scene.add.image(e.box.x, e.box.y, this.scene.textures.exists(key) ? key : '__DEFAULT')
        .setDisplaySize(e.box.w, e.box.h).setFlipX(!!e.placement.flipped).setVisible(this.scene.textures.exists(key));
      if (!this.scene.textures.exists(key)) {
        preloader.whenReady(key, () => { if (img.active) img.setTexture(key).setDisplaySize(e.box.w, e.box.h).setVisible(true); });
      }
      this._fronts.set(id, img);
    }
  }

  _buildZones() {
    for (const z of this._zones) z.destroy();
    this._zones = [];
    if (!this._tapZones) return;
    for (const s of this._spots()) {
      const r = TAP_SPOT_RADIUS;
      const zone = this.scene.add.zone(s.x, s.y - r, r * 2, r * 2).setInteractive({ useHandCursor: true });
      zone.on('pointerup', () => this._onSpotTap(s.perch));
      this._zones.push(zone);
    }
  }

  _feet() {
    const n = this.natalka;
    return { x: n.x, y: n.feetY ?? n.y + 120 };
  }

  _onSpotTap(perch) {
    if (isInputLocked(this.scene) || bikeSession.riding) return;
    if (this.perch === perch || this._taken().has(perch)) return;
    const s = this._spots().find((x) => x.perch === perch);
    if (!s) return;
    const f = this._feet();
    if (!this.perch && Math.hypot(f.x - s.x, f.y - s.y) > CLICK_SIT_RANGE) {
      showToast(this.scene, 'Podejdź bliżej');
      return;
    }
    this.sitOn(perch);
  }

  /** Sit / lie on this spot ("<placement id>/<seat>"). */
  sitOn(perch) {
    const s = this._spots().find((x) => x.perch === perch);
    if (!s || this._taken().has(perch)) return false;
    this.perch = perch;
    this._mySpot = s;
    this.walker.enabled = false;
    this.natalka.setWorldScale?.(s.scale ?? 1);   // the piece's size: it is drawn in the room's perspective, and she with it
    this.natalka.setMotion?.(0, 0);
    this.natalka.setPosition(s.x, s.y);
    this.natalka.setPose(s.pose, { flip: s.flip });
    audioManager.playSfx('click');
    this._broadcast?.({ x: s.x, y: s.y, facing: s.flip ? 'left' : 'right', mode: 'rest', perch, dir: null });
    this._register(STAND_LABEL, () => this.stand());
    return true;
  }

  _register(label, fn) {
    virtualInput.registerAction(label, fn);
    this._mine = virtualInput.getAction();
  }

  _clearMine() {
    if (this._mine && virtualInput.getAction() === this._mine) virtualInput.clearAction();
    this._mine = null;
  }

  /** Where standing up would put her (the sprite's centre), or null standing: while she sits or lies her (x, y) is the
   * pose anchor on the piece, so the rooms save this as her location instead (a reload put her inside the bed). */
  standingPoint() {
    if (!this.perch) return null;
    const s = this._mySpot ?? this.natalka;
    const e = this._entries.get(parsePerch(this.perch)?.placementId);
    const f = standFeet(s, e?.box ?? null);
    return { x: f.x, y: centreForFeet(this.scene, f.feetY) };
  }

  /** The free spot on this piece nearest the tap that opened its menu ({x, y}, world). */
  sitOnPiece(placementId, tap) {
    const e = this._entries.get(placementId);
    if (!e) return false;
    const s = tappedSpot(tap, spotsFor(e.placement, e.box), this._taken());
    return s ? this.sitOn(s.perch) : false;
  }

  /** Free spots on this piece (the menu leaves "Usiądź" out at 0). */
  freeSpotsOn(placementId) {
    const e = this._entries.get(placementId);
    if (!e) return 0;
    const taken = this._taken();
    return spotsFor(e.placement, e.box).filter((x) => !taken.has(x.perch)).length;
  }

  /** Whether you are on this piece. */
  isOn(placementId) { return parsePerch(this.perch)?.placementId === placementId; }

  stand() {
    if (!this.perch) return;
    const s = this._mySpot;
    const e = this._entries.get(parsePerch(this.perch)?.placementId);
    this.perch = null;
    this._mySpot = null;
    this.natalka.setPose(null);
    // On your feet at the foot of where you were: the spot's x, just below the piece.
    const f = standFeet(s ?? this.natalka, e?.box ?? null);
    this.natalka.setPosition(f.x, centreForFeet(this.scene, f.feetY));
    this.walker.enabled = true;
    this._broadcast?.({ x: this.natalka.x, y: this.natalka.y, facing: 'right', mode: 'walk', dir: this.natalka.facing8 });
    this._clearMine();
  }

  /** The server says someone else got there first. */
  onPerchTaken(perch) {
    if (!perch || perch !== this.perch) return;
    this.stand();
    showToast(this.scene, TAKEN_TOAST);
  }

  _movementPressed() {
    const c = this._cursors, k = this._wasd, v = virtualInput.vector;
    return !!(c?.left.isDown || c?.right.isDown || c?.up.isDown || c?.down.isDown
      || k?.A.isDown || k?.D.isDown || k?.W.isDown || k?.S.isDown
      || (v && (v.x !== 0 || v.y !== 0)));
  }

  update() {
    if (!this.natalka?.active) return;
    // The rooms draw their pieces again on every state change (new sprites): follow them.
    const next = this._read();
    if (FurnitureSeats._signature(next) !== this._sig) this.refresh(next);
    else this._entries = next;
    if (bikeSession.riding) return;   // the action slot belongs to the bike then
    if (isInputLocked(this.scene)) { this._clearMine(); return; }
    if (this.perch && !isTextInputFocused() && this._movementPressed()) { this.stand(); return; }
    const near = !this.perch && this._entries.size
      ? nearestFreeSpot(this._feet(), this._spots(), this._taken(), PERCH_RADIUS) : null;
    const step = seatAction({ cur: virtualInput.getAction(), mine: this._mine, perched: !!this.perch, near, offer: this._offer });
    if (step.op === 'register') {
      if (this.perch) this._register(STAND_LABEL, () => this.stand());
      else { this._offer = near.perch; this._register(step.label, () => this.sitOn(near.perch)); }
    } else if (step.op === 'clear') {
      this._offer = null;
      this._clearMine();
    }
  }

  // After the scene's y-sort: whoever sits on a piece is drawn just above it, and its front just above them —
  // a sofa's seat front over the legs, a bed's duvet over the body.
  sortDepths() {
    for (const [id, e] of this._entries) {
      if (!e.sprite.active) continue;
      const d = e.sprite.depth;
      this._fronts.get(id)?.setDepth(d + 0.2);
      if (this.isOn(id)) this.natalka.setDepth(d + 0.1);
    }
    for (const a of this._getPeers() ?? []) {
      if (a?.mode !== 'rest' || !a.perch || !a.sprite) continue;
      const e = this._entries.get(parsePerch(a.perch)?.placementId);
      if (e?.sprite.active) a.sprite.setDepth(e.sprite.depth + 0.1);
    }
  }

  destroy() {
    this.scene.events.off('update', this._onUpdate);
    this.scene.events.off('postupdate', this._onPost);
    this._offTaken?.();
    for (const z of this._zones) z.destroy();
    this._zones = [];
    for (const img of this._fronts.values()) img.destroy();
    this._fronts.clear();
    this._clearMine();
  }
}

/** [{placement, sprite}] from a room's placement sprites (each carries its `placement`). */
export const placementPairs = (sprites) => [...(sprites ?? [])].filter((sp) => sp?.placement).map((sp) => ({ placement: sp.placement, sprite: sp }));
